import { buildBulkApprovePayload } from '../../../components/OrderBulkActionsBar/bulkApprove';

/**
 * Statuses the `/updateStatus` endpoint accepts as a *source*, mirroring its own
 * `UPDATABLE_STATUSES`.
 *
 * `PENDING` and `WAITLIST` are absent on purpose: that route writes nothing but
 * the status column, and a load reaching `APPROVED` for the first time also
 * needs its warehouse resolved, its reservation cleared, the warehouse address
 * snapshotted, the delivery fee converted and the order totals recalculated.
 * Only the approve endpoint does that work, so a load in either status is
 * routed there instead of being flipped in place.
 *
 * Do not "fix" a stuck WAITLIST load by adding it here.
 */
export const STATUS_CHANGE_SOURCES = [
  'APPROVED',
  'ASSIGNED',
  'READY_FOR_PICKUP',
  'ENROUTE',
  'PREPAY_REQUIRED',
  'DELIVERED',
  'INVOICED',
  'PAID',
  'PALLET',
  'RAILCAR',
  'REJECTED',
  'CUSTOMER_CANCELLED',
  'ON_HOLD',
  'INVENTORY',
];

export const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'WAITLIST', label: 'Waitlist' },
  { value: 'APPROVED', label: 'Accepted' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup' },
  { value: 'ENROUTE', label: 'Enroute' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'INVOICED', label: 'Invoiced' },
  { value: 'CUSTOMER_CANCELLED', label: 'Cancelled' },
  { value: 'PREPAY_REQUIRED', label: 'Prepay Required' },
  { value: 'PICKED_UP', label: 'Picked Up' },
  { value: 'ON_HOLD', label: 'Frozen' },
  { value: 'INVENTORY', label: 'Inventory' },
];

/**
 * A load with a deleted product cannot move, and a PENDING one has to be
 * approved through the dialog rather than flipped in the table.
 */
export const canChangeStatus = (row: {
  status?: string;
  product?: { deletedAt?: string | null };
}): boolean => row.product?.deletedAt == null && row.status !== 'PENDING';

export const statusSelectTitle = (row: {
  status?: string;
  product?: { deletedAt?: string | null };
}): string | undefined => {
  if (!canChangeStatus(row)) {
    return row.product?.deletedAt != null
      ? 'Cannot change status of orders with deleted products'
      : 'Pending items must be approved';
  }

  // The pill is narrower than the longest label, so surface the full text.
  return STATUS_OPTIONS.find((o) => o.value === row.status)?.label;
};

/** The approval body, exactly as `buildBulkApprovePayload` produces it. */
type ApprovePayload = NonNullable<ReturnType<typeof buildBulkApprovePayload>>;

export type StatusChangePlan =
  | { kind: 'ignore' }
  | { kind: 'approve'; payload: ApprovePayload }
  | { kind: 'blocked'; message: string }
  | { kind: 'updateStatus'; status: string };

/**
 * What picking a status in the table dropdown should actually do.
 *
 * Three outcomes, and choosing wrongly is what produced the 2026-08-11 bug
 * where a WAITLIST load reported success and snapped straight back:
 *
 *   - `approve` — the load has never been approved, so it must go through the
 *     approve endpoint with a full payload, not the status endpoint.
 *   - `blocked` — same case, but the load is missing a warehouse or fulfilment
 *     method, so no payload can be built and the admin has to open it.
 *   - `updateStatus` — an ordinary transition the status endpoint can write.
 *
 * REJECTED is ignored here because rejecting needs a reason, which the table
 * has nowhere to collect.
 */
export const planStatusChange = (row: any, newStatus: string): StatusChangePlan => {
  if (!canChangeStatus(row)) {
    return { kind: 'ignore' };
  }

  if (newStatus === 'REJECTED') {
    return { kind: 'ignore' };
  }

  if (newStatus === row.status) {
    return { kind: 'ignore' };
  }

  if (newStatus === 'APPROVED' && !STATUS_CHANGE_SOURCES.includes(row.status)) {
    const payload = buildBulkApprovePayload(row);

    if (!payload) {
      return {
        kind: 'blocked',
        message:
          'This load needs a warehouse and fulfilment method before it can be approved — open it to finish.',
      };
    }

    return { kind: 'approve', payload };
  }

  return { kind: 'updateStatus', status: newStatus };
};
